import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = this.#findMatchingPriceContainer(event.detail.data.html);
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };

  /**
   * Finds the price container that corresponds to this element in fetched section HTML.
   * Multiple `product-price` elements can exist on a product page (e.g. main block + sticky ATC).
   * @param {Document} html
   * @returns {Element | null}
   */
  #findMatchingPriceContainer(html) {
    const blockId = this.dataset.blockId;

    if (blockId) {
      const match = html.querySelector(`product-price[data-block-id="${blockId}"] [ref="priceContainer"]`);
      if (match) return match;
    }

    if (this.classList.contains('c-atc-sticky__price')) {
      const match = html.querySelector('product-price.c-atc-sticky__price [ref="priceContainer"]');
      if (match) return match;
    }

    const section = this.closest('.shopify-section, dialog');

    if (section) {
      const productPrices = [...section.querySelectorAll('product-price')];
      const index = productPrices.indexOf(this);

      if (index >= 0) {
        const newProductPrices = [...html.querySelectorAll('product-price')];
        const corresponding = newProductPrices[index];

        if (corresponding) {
          return corresponding.querySelector('[ref="priceContainer"]');
        }
      }
    }

    return html.querySelector('product-price [ref="priceContainer"]');
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
